import React, { useMemo, useState } from 'react';
import { getAdminTheme } from './adminTheme';

interface AdminLoginScreenProps {
  isDarkMode: boolean;
  onLogin: (username: string, password: string) => Promise<void>;
}

const AdminLoginScreen: React.FC<AdminLoginScreenProps> = ({ isDarkMode, onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = useMemo(
    () => !isSubmitting && username.trim().length > 0 && password.trim().length > 0,
    [isSubmitting, username, password]
  );

  const theme = getAdminTheme(isDarkMode);

  return (
    <div
      id="admin-login"
      style={{
        minHeight: '100vh',
        backgroundColor: theme.pageBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <div
        id="admin-login-card"
        style={{
          width: '100%',
          maxWidth: 440,
          backgroundColor: theme.cardBg,
          border: `1px solid ${theme.border}`,
          borderRadius: theme.radius,
          padding: 24,
          boxSizing: 'border-box',
        }}
      >
        <div style={{ marginBottom: 16 }}>
          <div id="admin-login-title" style={{ fontSize: 22, fontWeight: 800, color: theme.text }}>
            Admin
          </div>
          <div style={{ marginTop: 6, fontSize: 14, color: theme.subText }}>
            Sign in to continue (we will harden security later)
          </div>
        </div>

        <form
          id="admin-login-form"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!canSubmit) return;
            setIsSubmitting(true);
            setError(null);
            try {
              await onLogin(username.trim(), password);
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Login failed');
            } finally {
              setIsSubmitting(false);
            }
          }}
        >
          <label style={{ display: 'block', fontSize: 13, color: theme.subText, marginBottom: 6 }}>Username or Email</label>
          <input
            id="admin-login-username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            style={{
              width: '100%',
              height: 40,
              padding: '0 12px',
              backgroundColor: theme.inputBg,
              border: `1px solid ${theme.border}`,
              borderRadius: theme.radius,
              outline: 'none',
              color: theme.text,
              boxSizing: 'border-box',
              marginBottom: 12,
            }}
          />

          <label style={{ display: 'block', fontSize: 13, color: theme.subText, marginBottom: 6 }}>Password</label>
          <input
            id="admin-login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            style={{
              width: '100%',
              height: 40,
              padding: '0 12px',
              backgroundColor: theme.inputBg,
              border: `1px solid ${theme.border}`,
              borderRadius: theme.radius,
              outline: 'none',
              color: theme.text,
              boxSizing: 'border-box',
              marginBottom: 16,
            }}
          />

          <button
            id="admin-login-submit"
            type="submit"
            disabled={!canSubmit}
            style={{
              width: '100%',
              height: 42,
              border: `1px solid ${canSubmit ? theme.primary : theme.border}`,
              borderRadius: theme.radius,
              backgroundColor: canSubmit ? theme.primary : theme.hoverBg,
              color: canSubmit ? '#FFFFFF' : theme.subText,
              fontWeight: 600,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
            }}
          >
            {isSubmitting ? 'Signing in…' : 'Sign in'}
          </button>

          {error ? (
            <div
              id="admin-login-error"
              style={{
                marginTop: 12,
                fontSize: 13,
                fontWeight: 800,
                color: isDarkMode ? '#FCA5A5' : '#B91C1C',
              }}
            >
              {error}
            </div>
          ) : null}
        </form>
      </div>
    </div>
  );
};

export default AdminLoginScreen;
